import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "../ui/button";
import { SalesPeriodPicker } from "../sales/SalesPeriodPicker";
import { presetRange } from "../sales/salesConstants";

/**
 * «Аналитика» — the landing's own visit metrics (ticket 8.5). Aggregate-only:
 * period cards, one row per Tallinn day, bounded split tables (top 50 + «other»),
 * a footnote with the three caveats and the rollup status. Every value renders
 * as text; referrers and UTM values never become links.
 */

const API_URL = process.env.REACT_APP_API_URL || "";

interface Counters {
  views: number;
  uniqueVisitors: number;
  ctaClicks: number;
  demoInteractions: number;
  loginStarted: number;
  loginSucceeded: number;
  convertedVisitors: number;
}
interface DayRow extends Counters {
  day: string;
  conversion: number | null;
}
interface SplitRow extends Counters {
  key: string;
}
type SplitKey = "audience" | "language" | "referrer" | "utm_source" | "utm_medium" | "utm_campaign" | "cta" | "demo";
export interface LandingReport {
  period: { from: string; to: string };
  totals: Counters & { guestUniqueVisitors: number; guestConvertedVisitors: number; conversion: number | null };
  audienceViews: { guest: number; customer: number; admin: number };
  perDay: DayRow[];
  splits: Record<SplitKey, SplitRow[]>;
  rollup: { lastRolledAt: string | null; oldestUnfinalizedDay: string | null };
}

/**
 * A split table answers one question — which sources, languages or buttons pull
 * their weight — so it carries three or four columns, not the full counter set.
 * `action` marks the two splits counted in clicks rather than views.
 */
const SPLITS: { key: SplitKey; titleKey: string; action?: "ctaClicks" | "demoInteractions" }[] = [
  { key: "audience", titleKey: "admin.analytics.splits.audience" },
  { key: "language", titleKey: "admin.analytics.splits.language" },
  { key: "referrer", titleKey: "admin.analytics.splits.referrer" },
  { key: "utm_source", titleKey: "admin.analytics.splits.utmSource" },
  { key: "utm_medium", titleKey: "admin.analytics.splits.utmMedium" },
  { key: "utm_campaign", titleKey: "admin.analytics.splits.utmCampaign" },
  { key: "cta", titleKey: "admin.analytics.splits.cta", action: "ctaClicks" },
  { key: "demo", titleKey: "admin.analytics.splits.demo", action: "demoInteractions" },
];

/** Converted visitors over unique visitors of that key, one decimal; «—» on a zero denominator. */
function conversionOf(row: Counters): number | null {
  return row.uniqueVisitors > 0 ? Math.round((row.convertedVisitors / row.uniqueVisitors) * 1000) / 10 : null;
}

const percent = (value: number | null): string => (value === null ? "—" : `${value.toFixed(1)} %`);

const LOCALES: Record<string, string> = { et: "et-EE", en: "en-GB", ru: "ru-RU" };
const localeOf = (lang: string): string => LOCALES[lang.slice(0, 2)] ?? "et-EE";

/**
 * A Tallinn calendar day as people read it: "сб, 12.09.2026". The weekday earns
 * its place in the per-day table — weekend dips are half of what the operator
 * looks for. The API and the CSV keep the sortable ISO form.
 */
export function formatDay(day: string, lang: string, withWeekday = true): string {
  const at = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(at.getTime())) return day;
  const text = new Intl.DateTimeFormat(localeOf(lang), {
    timeZone: "UTC",
    ...(withWeekday ? { weekday: "short" as const } : {}),
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(at);
  // Russian appends " г." to a numeric year — noise in a table cell, kept in the status sentence.
  return text.replace(/\s*г\.$/, "");
}

/** An instant for the rollup status line: "12 сент. 2026 г., 05:30". */
export function formatMoment(iso: string, lang: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return new Intl.DateTimeFormat(localeOf(lang), { dateStyle: "medium", timeStyle: "short" }).format(at);
}
/** The rollup's fold bucket — the same out-of-domain sentinel as the backend vocabulary. */
export const OTHER_KEY = "~other";

export function csvUrl(from: string, to: string, by: string): string {
  const params = new URLSearchParams({ from, to, by });
  return `${API_URL}/api/admin/metrics/landing.csv?${params.toString()}`;
}

export function LandingAnalyticsTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [[from, to], setPeriod] = useState<[string, string]>(() => presetRange("currentMonth"));
  const [report, setReport] = useState<LandingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A slower response for a previous period must not overwrite a newer one.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    // Never show the previous period's numbers under the new dates (code review r2):
    // cards, tables and CSV links disappear until the requested period has loaded.
    setReport(null);
    try {
      const res = await axios.get<{ success: boolean; data: LandingReport }>(
        `${API_URL}/api/admin/metrics/landing?from=${from}&to=${to}`,
        { withCredentials: true }
      );
      if (seq !== requestSeq.current) return;
      setReport(res.data.data);
    } catch {
      if (seq !== requestSeq.current) return;
      setError(t("admin.analytics.loadFailed"));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [from, to, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const keyLabel = (split: SplitKey, key: string): string => {
    if (key === OTHER_KEY) return t("admin.analytics.other");
    if (key === "") return split === "referrer" ? t("admin.analytics.direct") : t("admin.analytics.none");
    if (split === "audience") return t(`admin.analytics.audience.${key}`, key);
    if (split === "cta") return t(`admin.analytics.cta.${key}`, key);
    return key;
  };

  const card = (label: string, value: number | string) => (
    <div className="p-4 rounded-xl border border-border bg-card">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold text-foreground mt-1">{value}</div>
    </div>
  );

  const th = "px-3 py-2 text-right font-medium whitespace-nowrap";
  const counterHeaders = (
    <>
      <th className={th}>{t("admin.analytics.col.views")}</th>
      <th className={th}>{t("admin.analytics.col.unique")}</th>
      <th className={th}>{t("admin.analytics.col.cta")}</th>
      <th className={th}>{t("admin.analytics.col.demo")}</th>
      <th className={th}>{t("admin.analytics.col.started")}</th>
      <th className={th}>{t("admin.analytics.col.succeeded")}</th>
      <th className={th}>{t("admin.analytics.col.converted")}</th>
    </>
  );
  const counterCells = (c: Counters) => (
    <>
      <td className="px-3 py-2 text-right tabular-nums">{c.views}</td>
      <td className="px-3 py-2 text-right tabular-nums">{c.uniqueVisitors}</td>
      <td className="px-3 py-2 text-right tabular-nums">{c.ctaClicks}</td>
      <td className="px-3 py-2 text-right tabular-nums">{c.demoInteractions}</td>
      <td className="px-3 py-2 text-right tabular-nums">{c.loginStarted}</td>
      <td className="px-3 py-2 text-right tabular-nums">{c.loginSucceeded}</td>
      <td className="px-3 py-2 text-right tabular-nums">{c.convertedVisitors}</td>
    </>
  );

  // Export what is on screen: the displayed report's period (only rendered once it loaded).
  const shown = report?.period ?? { from, to };
  const exportLink = (by: string, label: string) => (
    <a
      key={by}
      href={csvUrl(shown.from, shown.to, by)}
      download
      className="text-xs text-primary underline underline-offset-2 hover:text-primary/80"
    >
      {label}
    </a>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SalesPeriodPicker from={from} to={to} onChange={(f, tt) => setPeriod([f, tt])} />
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {t("admin.analytics.refresh")}
        </Button>
      </div>

      {error && <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">{error}</div>}
      {loading && <div className="text-sm text-muted-foreground">{t("common.loading", "…")}</div>}

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-cards">
            {card(t("admin.analytics.cards.views"), report.totals.views)}
            {card(t("admin.analytics.cards.visitorDays"), report.totals.uniqueVisitors)}
            {card(t("admin.analytics.cards.cta"), report.totals.ctaClicks)}
            {card(t("admin.analytics.cards.demo"), report.totals.demoInteractions)}
            {card(t("admin.analytics.cards.started"), report.totals.loginStarted)}
            {card(t("admin.analytics.cards.succeeded"), report.totals.loginSucceeded)}
            {card(t("admin.analytics.cards.conversion"), percent(report.totals.conversion))}
            {card(
              t("admin.analytics.cards.audienceViews"),
              `${report.audienceViews.guest} / ${report.audienceViews.customer} / ${report.audienceViews.admin}`
            )}
          </div>

          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">{t("admin.analytics.perDay")}</h3>
              {exportLink("day", t("admin.analytics.exportCsv"))}
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-[11px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">{t("admin.analytics.col.day")}</th>
                    {counterHeaders}
                    <th className={th}>{t("admin.analytics.col.conversion")}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.perDay.map((d) => (
                    <tr key={d.day} className="border-t border-border">
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{formatDay(d.day, lang)}</td>
                      {counterCells(d)}
                      <td className="px-3 py-2 text-right tabular-nums">{percent(d.conversion)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="grid gap-6 lg:grid-cols-2">
            {SPLITS.map(({ key, titleKey, action }) => {
              const rows = report.splits[key] ?? [];
              const countLabel = action === "ctaClicks" ? "admin.analytics.col.clicks" : "admin.analytics.col.actions";
              return (
                <section key={key} data-testid={`split-${key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{t(titleKey)}</h3>
                    {exportLink(key, t("admin.analytics.exportCsv"))}
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-[11px] uppercase text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">{t("admin.analytics.col.key")}</th>
                          <th className={th}>{action ? t(countLabel) : t("admin.analytics.col.views")}</th>
                          <th className={th}>{t("admin.analytics.col.unique")}</th>
                          {!action && <th className={th}>{t("admin.analytics.col.conversion")}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 && (
                          <tr>
                            <td className="px-3 py-3 text-muted-foreground" colSpan={action ? 3 : 4}>
                              {t("admin.analytics.empty")}
                            </td>
                          </tr>
                        )}
                        {rows.map((r) => (
                          <tr key={r.key} className="border-t border-border">
                            <td className="px-3 py-2 break-all">{keyLabel(key, r.key)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{action ? r[action] : r.views}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.uniqueVisitors}</td>
                            {!action && <td className="px-3 py-2 text-right tabular-nums">{percent(conversionOf(r))}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
          </div>

          <footer className="text-xs text-muted-foreground space-y-1" data-testid="analytics-footnote">
            <p>{t("admin.analytics.note.splits")}</p>
            <p>{t("admin.analytics.note.sameDay")}</p>
            <p>{t("admin.analytics.note.received")}</p>
            <p>
              {t("admin.analytics.lastRollup")}:{" "}
              {report.rollup.lastRolledAt ? formatMoment(report.rollup.lastRolledAt, lang) : t("admin.analytics.never")}
              {report.rollup.oldestUnfinalizedDay && (
                <>
                  {" · "}
                  {t("admin.analytics.oldestUnfinalized")}: {formatDay(report.rollup.oldestUnfinalizedDay, lang, false)}
                </>
              )}
            </p>
          </footer>
        </>
      )}
    </div>
  );
}
